// src/components/HowItWorks.js

import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export default function HowItWorks() {
  const steps = [
    {
      img: '/assets/form.png',
      alt: 'Submit Request',
      title: 'Submit a Request',
      desc: 'Tell us your event needs through a short enquiry form',
      number: '01'
    },
    {
      img: '/assets/sparkle.png',
      alt: 'Get Matches',
      title: 'Get Vendor Matches',
      desc: 'We will recommend verified local vendors based on your location and type.',
      number: '02'
    },
    {
      img: '/assets/handshake.png',
      alt: 'Connect',
      title: 'Connect & Finalize',
      desc: 'Reach out to your chosen vendors, discuss services, and lock your date',
      number: '03'
    },
    {
      img: '/assets/diya.png',
      alt: 'Celebrate',
      title: 'Celebrate Stress-Free',
      desc: 'Enjoy your big day with all services handled smoothly and traditionally',
      number: '04'
    },
  ];

  return (
    <section
      id="how-it-works"
      className="relative bg-gradient-to-b from-white via-slate-50 to-white py-24 px-6 md:px-20 overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute top-1/4 left-1/4 w-[200px] h-[200px] bg-gradient-to-r from-orange-100 to-amber-100 rounded-full blur-3xl opacity-20 -z-10"></div>
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full blur-3xl opacity-15 -z-10"></div>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-3 -z-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f97316' fill-opacity='0.05'%3E%3Cpath d='m20 20 20-20v40z'/%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <div className="max-w-7xl mx-auto">
        {/* Enhanced header section */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full text-sm font-medium text-orange-700 mb-6">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            Simple Process
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-slate-800">How It</span>
            <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent"> Works</span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            From request to celebration - we make finding trusted vendors simple and stress-free
          </p>
        </div>

        {/* Steps grid with enhanced design */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {steps.map(({ img, alt, title, desc, number }, i) => (
            <div key={title} className="relative group">
              {/* Connection line for desktop */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-px bg-gradient-to-r from-orange-300 to-transparent z-0"></div>
              )}
              
              <div className={`
                relative
                p-8
                rounded-2xl
                bg-white/80
                backdrop-blur-sm
                border border-slate-200/50
                shadow-lg
                hover:shadow-2xl
                transform
                transition-all
                duration-300
                hover:scale-105
                hover:-translate-y-2
                cursor-pointer
                text-left
                group-hover:border-orange-200
                z-10
              `}>
                {/* Step number indicator */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  {number}
                </div>

                {/* Icon and title section */}
                <div className="flex items-start space-x-4 mb-6 pt-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-200 to-amber-200 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                    <div className="relative bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-2xl border border-orange-100 group-hover:border-orange-200 transition-colors duration-300">
                      <img
                        src={img}
                        alt={alt}
                        loading="lazy"
                        className="w-12 h-12 object-contain flex-shrink-0 drop-shadow-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-bold text-xl text-slate-800 group-hover:text-slate-900 transition-colors duration-300 mb-2">
                      {title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">
                      {desc}
                    </p>
                  </div>
                </div>

                {/* Hover indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                </div>
                
                {/* Subtle gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/0 to-amber-50/0 group-hover:from-orange-50/30 group-hover:to-amber-50/20 rounded-2xl transition-all duration-300 pointer-events-none"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Call-to-action section */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center space-x-8 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl px-8 py-6 shadow-lg">
            <div className="flex items-center space-x-2 text-slate-600">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">Free to use</span>
            </div>
            
            <div className="h-4 w-px bg-slate-300"></div>
            
            <div className="flex items-center space-x-2 text-slate-600">
              <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">Instant notifications</span>
            </div>
            
            <div className="h-4 w-px bg-slate-300"></div>
            
            <div className="flex items-center space-x-2 text-slate-600">
              <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">Verified vendors only</span>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced scroll-down indicator */}
      <a
        href="#why-trust-us"
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 group"
        aria-label="Scroll to Why Indian Families Trust Us"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm text-slate-500 font-medium group-hover:text-slate-700 transition-colors duration-200 opacity-0 group-hover:opacity-100">
            Learn why families trust us
          </span>
          <div className="p-3 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-white/50 group-hover:shadow-xl transition-all duration-200 group-hover:bg-orange-50/80">
            <ChevronDownIcon className="w-6 h-6 text-slate-600 animate-bounce group-hover:text-orange-600 transition-colors duration-200" />
          </div>
        </div>
      </a>
    </section>
  );
}